/**
 * ColorPicker.tsx
 *
 * Grid de selección de color de la paleta del sistema.
 * Componente puro — sin opinión sobre su contenedor.
 * Úsalo dentro de un Popover, Modal, o formulario según el contexto.
 *
 * Envía el token string al seleccionar (ej: "red-ember").
 * El valor oklch se resuelve en runtime via getPaletteColor(token).
 *
 * Uso básico:
 *   <ColorPicker onSelect={(token) => handleChange(token)} />
 *
 * Con color seleccionado:
 *   <ColorPicker value="red-ember" onSelect={(token) => handleChange(token)} />
 */

import { useTranslation } from 'react-i18next';
import { cva } from 'class-variance-authority';
import { cn } from '@/core/lib/utils';
import { PALETTE_BY_GROUP, type PaletteColor, type PaletteGroup } from '@/core/lib/colors';

/* ── Constantes ─────────────────────────────────────────────── */

/** Orden visual de los grupos en el grid */
const GROUP_ORDER: PaletteGroup[] = [
  'red',
  'coral',
  'orange',
  'yellow',
  'lime',
  'green',
  'teal',
  'cyan',
  'indigo',
  'purple',
  'magenta',
  'pink',
  'gray',
];

/* ── Variantes ──────────────────────────────────────────────── */

const swatchVariants = cva(
  [
    'relative rounded-sm cursor-pointer transition-transform duration-100',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
    'hover:scale-110 active:scale-95',
  ],
  {
    variants: {
      size: {
        sm: 'size-5',
        md: 'size-6',
        lg: 'size-7',
      },
      selected: {
        true: 'ring-2 ring-offset-2 ring-offset-background scale-110',
        false: '',
      },
    },
    defaultVariants: { size: 'md', selected: false },
  },
);

/* ── Props ──────────────────────────────────────────────────── */

export interface ColorPickerProps {
  /** Token del color actualmente seleccionado (ej: "red-ember") */
  value?: string;
  /** Callback al seleccionar un color — recibe el token string */
  onSelect: (token: string) => void;
  /** Tamaño de cada swatch */
  size?: 'sm' | 'md' | 'lg';
  /** Deshabilita la interacción */
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

/* ── Sub-componente: Swatch individual ──────────────────────── */

interface SwatchProps {
  color: PaletteColor;
  selected: boolean;
  size: 'sm' | 'md' | 'lg';
  disabled: boolean;
  onSelect: (token: string) => void;
  'data-testid'?: string;
}

function Swatch({ color, selected, size, disabled, onSelect, 'data-testid': testId }: SwatchProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={color.label}
      disabled={disabled}
      data-testid={testId ? `${testId}-${color.token}` : undefined}
      className={cn(
        swatchVariants({ size, selected }),
        disabled && 'opacity-40 cursor-not-allowed hover:scale-100',
      )}
      style={{ background: color.value }}
      onClick={() => !disabled && onSelect(color.token)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          !disabled && onSelect(color.token);
        }
      }}
    >
      {/* Indicador de selección — checkmark sutil */}
      {selected && (
        <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2 5L4 7L8 3"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      )}
    </button>
  );
}

/* ── Componente principal ───────────────────────────────────── */

export function ColorPicker({
  value,
  onSelect,
  size = 'md',
  disabled = false,
  className,
  'data-testid': testId,
}: ColorPickerProps) {
  const { t } = useTranslation('colorPicker');

  return (
    <div
      role="radiogroup"
      aria-label={t('ariaLabel')}
      className={cn('flex flex-col gap-1.5', className)}
    >
      {GROUP_ORDER.map((group) => (
        <div key={group} className="flex gap-1">
          {PALETTE_BY_GROUP[group].map((color) => (
            <Swatch
              key={color.token}
              color={color}
              selected={value === color.token}
              size={size}
              disabled={disabled}
              onSelect={onSelect}
              data-testid={testId}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
